import type {Observation} from '@pamosi/shared';

const FIELDS = {
    bp_systolic: {codes:['systolic_bp'],unit:'mmHg',label:'Systolic BP',min:40,max:300,integer:true},
    bp_diastolic: {codes:['diastolic_bp'],unit:'mmHg',label:'Diastolic BP',min:20,max:200,integer:true},
    heart_rate: {codes:['pulse_bpm','pulse','heart_rate'],unit:'bpm',label:'Heart rate',min:20,max:300,integer:true},
    resp_rate: {codes:['respiratory_rate','resp_rate'],unit:'/min',label:'Respiratory rate',min:4,max:80,integer:true},
    temp_c: {codes:['temp_c'],unit:'°C',label:'Temperature',min:25,max:45,integer:false},
    spo2: {codes:['spo2','o2_sat'],unit:'%',label:'SpO₂',min:50,max:100,integer:true},
    weight_kg: {codes:['weight_kg'],unit:'kg',label:'Weight',min:0.3,max:400,integer:false},
    height_cm: {codes:['height_cm'],unit:'cm',label:'Height',min:20,max:260,integer:false},
    pain_score: {codes:['pain_score'],unit:'0–10',label:'Pain score',min:0,max:10,integer:true},
} as const;
export type TriageVitalKey=keyof typeof FIELDS;
export type ObservationVital={field:TriageVitalKey;label:string;unit:string;observation:Observation};

// Project existing readings only. No clinical writes, cross-visit copying or unit conversion.
export function observationVitals(observations:Observation[],encounterId:string,patientId:string):ObservationVital[]{
    return (Object.keys(FIELDS) as TriageVitalKey[]).flatMap(field=>{
        const spec=FIELDS[field];
        const matching=observations.filter(o=>o.encounter_id===encounterId&&o.patient_id===patientId&&!o.superseded_by_id&&(spec.codes as readonly string[]).includes(o.code));
        matching.sort((a,b)=>Date.parse(b.observed_at)-Date.parse(a.observed_at)||Date.parse(b.created_at)-Date.parse(a.created_at)||b.id.localeCompare(a.id));
        const o=matching[0],n=o?.value_numeric;
        // An invalid newest measurement must not make an older reading look current.
        if(!o||!Number.isFinite(Date.parse(o.observed_at))||n==null||!Number.isFinite(n)||n<spec.min||n>spec.max||(spec.integer&&!Number.isInteger(n)))return [];
        const units=field==='temp_c'?['°C','C','celsius']:field==='resp_rate'?['/min','breaths/min']:field==='pain_score'?['0–10','0-10','score']:[spec.unit];
        if(!o.unit||!units.includes(o.unit))return [];
        return [{field,label:spec.label,unit:spec.unit,observation:o}];
    });
}
export function fillEmptyTriageVitals<T extends Record<TriageVitalKey,string>>(current:T,sources:ObservationVital[]):T{
    const next={...current};
    for(const source of sources)if(next[source.field].trim()==='')next[source.field]=String(source.observation.value_numeric);
    return next;
}
